
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Check, Trash2, Plus, Minus, ArrowRight, ChevronsUp, ArrowLeft } from 'lucide-react';
import { CartItem } from '../types';

interface CartViewProps {
    cartItems: CartItem[];
    isDark: boolean;
    onUpdateQuantity: (id: number | string, delta: number) => void;
    onRemove: (id: number | string) => void;
    onToggleSelection: (id: number | string) => void;
    onBack: () => void;
    onOpenDetails: (product: any) => void;
}

export const CartView: React.FC<CartViewProps> = ({
    cartItems,
    isDark,
    onUpdateQuantity,
    onRemove,
    onToggleSelection,
    onBack,
    onOpenDetails
}) => {
    const navigate = useNavigate();
    const activeCartItems = cartItems.filter(item => item.selected);
    const inactiveCartItems = cartItems.filter(item => !item.selected);
    const cartItemsCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

    const getTotalPrice = () => {
        return cartItems
            .filter(item => item.selected)
            .reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    return (
        <div className="flex flex-col h-full w-full overflow-hidden max-w-[1400px] mx-auto relative">

            {/* 1️⃣ Header Section with Back Button */}
            <div className="w-full px-4 pb-2 shrink-0 flex items-center justify-between z-10" style={{ direction: 'rtl' }}>
                <h2 className={`text-2xl md:text-3xl font-black ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    سلة المشتريات ({cartItemsCount})
                </h2>
                <button
                    onClick={onBack}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-black text-xs md:text-sm backdrop-blur-sm md:backdrop-blur-md shadow-sm border transition-all active:scale-95 hover:scale-105 ${isDark ? 'bg-white/5 text-white border-white/20 hover:bg-white/10' : 'bg-white text-slate-800 border-slate-200 hover:bg-slate-50'}`}
                    style={{ direction: 'ltr' }}
                >
                    <ArrowLeft size={16} />
                    <span>عودة</span>
                </button>
            </div>

            {cartItemsCount === 0 ? (
                // Empty State
                <div className="flex-1 flex flex-col items-center justify-center min-h-0 overflow-y-auto">
                    <div className={`w-40 h-40 md:w-60 md:h-60 rounded-full flex items-center justify-center mb-6 relative ${isDark ? 'bg-white/5' : 'bg-slate-200'}`}>
                        <ShoppingCart size={80} className={`opacity-60 md:w-[120px] md:h-[120px] ${isDark ? 'text-white/30' : 'text-slate-400'}`} strokeWidth={1.5} />
                    </div>

                    <h2 className={`text-2xl md:text-4xl font-black mb-6 text-center ${isDark ? 'text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.6)]' : 'text-slate-800'}`}>
                        عربة التسوق فارغة!
                    </h2>

                    <button
                        onClick={onBack}
                        className={`px-12 py-3 rounded-full border-2 font-black text-lg md:text-2xl transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(255,255,255,0.2)] ${isDark ? 'border-white text-white hover:bg-white hover:text-black' : 'border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white'}`}
                    >
                        تسوق الآن
                    </button>
                </div>
            ) : (
                <>
                    {/* 2️⃣ Scrollable Content (Products) - Added min-h-0 and overscroll-contain */}
                    <div className="flex-1 overflow-y-auto min-h-0 w-full px-2 py-2 space-y-4 custom-scrollbar pb-4 overscroll-contain">

                        {/* Active Items */}
                        {activeCartItems.map((item) => (
                            <div
                                key={item.id}
                                className={`w-full p-4 rounded-[2rem] flex items-center gap-4 transition-all animate-fade-in-up border ${isDark ? 'bg-[#1a1c1e] border-white/10' : 'bg-white border-slate-100 shadow-sm'} group/item`}
                                style={{ direction: 'rtl' }}
                            >
                                {/* Checkbox for Selection */}
                                <div
                                    onClick={() => onToggleSelection(item.id)}
                                    className={`shrink-0 cursor-pointer transition-transform active:scale-90`}
                                >
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${item.selected ? 'bg-blue-500 border-blue-500' : (isDark ? 'border-white/30' : 'border-slate-300')}`}>
                                        {item.selected && <Check size={14} className="text-white" strokeWidth={3} />}
                                    </div>
                                </div>

                                {/* Image */}
                                <div
                                    onClick={() => onOpenDetails({ ...item, id: item.productId || item.id } as any)}
                                    className={`w-20 h-20 md:w-24 md:h-24 shrink-0 rounded-2xl overflow-hidden p-2 cursor-pointer hover:scale-105 transition-transform ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}
                                >
                                    <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply dark:mix-blend-normal" />
                                </div>

                                <div
                                    onClick={() => onOpenDetails({ ...item, id: item.productId || item.id } as any)}
                                    className="flex-1 flex flex-col gap-0.5 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                                >
                                    <h3 className={`font-black text-xs md:text-lg line-clamp-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>{item.name}</h3>
                                    <div className="flex flex-wrap gap-1.5 mt-0.5">
                                        {item.selectedColor && (
                                            <span className={`px-2 py-0.5 rounded-full text-[8px] md:text-[10px] font-bold border ${isDark ? 'bg-white/5 border-white/10 text-white/60' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
                                                اللون: {item.selectedColor}
                                            </span>
                                        )}
                                        {item.selectedSize && (
                                            <span className={`px-2 py-0.5 rounded-full text-[8px] md:text-[10px] font-bold border ${isDark ? 'bg-white/5 border-white/10 text-white/60' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
                                                المقاس: {item.selectedSize}
                                            </span>
                                        )}
                                    </div>
                                    <p className={`text-[9px] md:text-xs font-bold line-clamp-1 mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{item.description}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className={`text-xs md:text-lg font-black text-blue-500`}>
                                            {(item.price * item.quantity).toLocaleString()} <span className="text-[9px] text-slate-400">ج.م</span>
                                        </span>
                                        {item.oldPrice && item.oldPrice > item.price && (
                                            <span className={`text-[9px] md:text-xs font-bold line-through ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                                                {(item.oldPrice * item.quantity).toLocaleString()}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Controls */}
                                <div className="flex flex-col items-end gap-1.5 shrink-0">
                                    <button
                                        onClick={() => onRemove(item.id)}
                                        className="p-1 text-red-400 hover:bg-red-500/10 rounded-full transition-colors"
                                    >
                                        <Trash2 size={14} className="md:w-4 md:h-4" />
                                    </button>

                                    <div className={`flex items-center gap-1.5 md:gap-2 rounded-full p-0.5 md:p-1 border ${isDark ? 'bg-black/30 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                        <button
                                            onClick={() => onUpdateQuantity(item.id, 1)}
                                            className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-white shadow-sm hover:bg-slate-100 text-slate-700'}`}
                                        >
                                            <Plus size={12} />
                                        </button>
                                        <span className={`w-3 md:w-4 text-center font-bold text-xs md:text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>{item.quantity}</span>
                                        <button
                                            onClick={() => onUpdateQuantity(item.id, -1)}
                                            className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-white shadow-sm hover:bg-slate-100 text-slate-700'}`}
                                        >
                                            <Minus size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Inactive / Saved Items Section */}
                        {inactiveCartItems.length > 0 && (
                            <>
                                <div className="flex items-center justify-center my-4 opacity-50">
                                    <div className={`h-px w-full ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}></div>
                                    <span className={`px-4 text-xs font-bold whitespace-nowrap ${isDark ? 'text-white/40' : 'text-slate-400'}`}>غير محددة للشراء</span>
                                    <div className={`h-px w-full ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}></div>
                                </div>

                                {inactiveCartItems.map((item) => (
                                    <div
                                        key={item.id}
                                        className={`w-full p-4 rounded-[2rem] flex items-center gap-4 transition-all animate-slide-up border opacity-70 hover:opacity-100 ${isDark ? 'bg-[#1a1c1e]/50 border-white/5' : 'bg-slate-50 border-slate-100'}`}
                                        style={{ direction: 'rtl' }}
                                    >
                                        {/* Checkbox for Selection (Inactive) */}
                                        <div
                                            onClick={() => onToggleSelection(item.id)}
                                            className={`shrink-0 cursor-pointer transition-transform active:scale-90`}
                                        >
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isDark ? 'border-white/30' : 'border-slate-300'}`}>
                                                {/* Empty circle */}
                                            </div>
                                        </div>

                                        {/* Image (Grayscale slightly) */}
                                        <div
                                            onClick={() => onOpenDetails({ ...item, id: item.productId || item.id } as any)}
                                            className={`w-20 h-20 shrink-0 rounded-2xl overflow-hidden p-2 grayscale opacity-80 cursor-pointer hover:scale-105 transition-transform ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}
                                        >
                                            <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply dark:mix-blend-normal" />
                                        </div>

                                        {/* Info */}
                                        <div
                                            onClick={() => onOpenDetails({ ...item, id: item.productId || item.id } as any)}
                                            className="flex-1 flex flex-col gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                                        >
                                            <h3 className={`font-black text-sm md:text-lg line-clamp-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{item.name}</h3>
                                            <div className="flex flex-wrap gap-1">
                                                {item.selectedColor && (
                                                    <span className={`px-1.5 py-0.5 rounded-full text-[7px] md:text-[9px] font-bold border ${isDark ? 'bg-white/5 border-white/5 text-white/30' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                                                        {item.selectedColor}
                                                    </span>
                                                )}
                                                {item.selectedSize && (
                                                    <span className={`px-1.5 py-0.5 rounded-full text-[7px] md:text-[9px] font-bold border ${isDark ? 'bg-white/5 border-white/5 text-white/30' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                                                        {item.selectedSize}
                                                    </span>
                                                )}
                                            </div>
                                            <div className={`text-sm font-bold mt-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                                                {(item.price * item.quantity).toLocaleString()} <span className="text-[10px]">ج.م</span>
                                            </div>
                                        </div>

                                        {/* Controls (Simplified) */}
                                        <div className="flex flex-col items-end gap-2 shrink-0">
                                            <button
                                                onClick={() => onRemove(item.id)}
                                                className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-full transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    {/* 3️⃣ Fixed Checkout Footer */}
                    <div className={`w-full p-4 pb-[72px] md:p-6 md:pb-6 rounded-t-[2.5rem] border-t backdrop-blur-md md:backdrop-blur-xl shrink-0 ${isDark ? 'bg-[#121418]/95 border-white/10' : 'bg-white/95 border-slate-200 shadow-[0_-10px_40px_rgba(0,0,0,0.05)]'}`}>
                        {(() => {
                            const totalPrice = activeCartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
                            const totalOldPrice = activeCartItems.reduce((t, item) => {
                                const old = item.oldPrice || item.price;
                                return t + (old * item.quantity);
                            }, 0);
                            const totalDiscount = totalOldPrice - totalPrice;
                            const totalSaved = totalDiscount;
                            const totalPointsEarned = Math.min(activeCartItems.reduce((t, item) => t + ((item.giftPoints || 0) * item.quantity), 0), 50);

                            return (
                                <div style={{ direction: 'rtl' }} className="space-y-2">
                                    {/* Original price before discount */}
                                    {totalDiscount > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span className={`text-sm font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>السعر قبل الخصم</span>
                                            <span className={`text-sm font-bold line-through ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                                {totalOldPrice.toLocaleString()} ج.م
                                            </span>
                                        </div>
                                    )}

                                    {/* Discount amount */}
                                    {totalDiscount > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-bold text-red-400 flex items-center gap-1">
                                                🏷️ الخصم
                                            </span>
                                            <span className="text-sm font-black text-red-400">
                                                - {totalDiscount.toLocaleString()} ج.م
                                            </span>
                                        </div>
                                    )}



                                    {/* Gift Points */}
                                    {totalPointsEarned > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-bold text-amber-500 flex items-center gap-1">
                                                ✨ نقاط هدايا
                                            </span>
                                            <span className="text-sm font-black text-amber-500">
                                                + {totalPointsEarned.toLocaleString()} نقطة
                                            </span>
                                        </div>
                                    )}

                                    {/* Divider */}
                                    {(totalDiscount > 0 || totalPointsEarned > 0) && (
                                        <div className={`h-px ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />
                                    )}

                                    {/* Total to pay */}
                                    <div className="flex justify-between items-center">
                                        <span className={`text-lg md:text-xl font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>الإجمالي ({activeCartItems.length})</span>
                                        <span className={`text-2xl md:text-4xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                            {totalPrice.toLocaleString()} <span className="text-sm text-blue-500">ج.م</span>
                                        </span>
                                    </div>

                                    {/* Savings badge */}
                                    {totalSaved > 0 && (
                                        <div className="flex justify-center">
                                            <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 px-4 py-1.5 rounded-full text-xs md:text-sm font-black flex items-center gap-1.5">
                                                ✅ تم توفير {totalSaved.toLocaleString()} ج.م من الخصومات
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                        <button
                            onClick={() => navigate('/checkout')}
                            className="w-full py-4 mt-3 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black text-xl shadow-lg shadow-blue-500/30 active:scale-[0.98] transition-all hover:brightness-110 flex items-center justify-center gap-2"
                        >
                            <span>إتمام الشراء</span>
                            <ArrowRight size={20} className="rotate-180" />
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};
